import React, { useEffect, useState, useCallback, useRef } from "react";
import Modal from "src/components/common/Modal";
import { useItemOptionMutations } from "../hooks/useItemOptionMutations";
import { convertCentsToDollars } from "src/helpers";
import MenuItemOptionElementForm from "./MenuItemOptionElementForm";
import MenuItemOptionElementRow from "./MenuItemOptionElementRow";
import Button from "src/components/common/Button";
import FormRowVertical from "src/components/common/FormRowVertical";
import Input, { InputAsEnum, InputTypeEnum } from "src/components/common/Input";
import Toggle from "src/components/common/Toggle";
import { AddMenuItemOptionSuccessPayload, MenuItemOptionModalProps } from "src/interfaces/menu-items-props.interface";
import { AddItemOptionInput, MenuItemOptionElement } from "src/interfaces/menu.interface";
import { useOptionElementHandlers } from "../hooks/useOptionElementHandlers";
import { useMenuItemOptionModalLogic } from "../hooks/useMenuItemOptionModalLogic";
import { MenuItemOptionElementField, InputValue } from "src/types/menu-items.type";
import MenuItemLocaleFields from "src/components/common/MenuItemLocaleFields";
import { useStorePosLocales } from "src/hooks/useStorePosLocales";
import {
  buildLocaleTranslationMap,
  buildTranslationsPayload,
  LocaleTranslationMap,
  syncLegacyNameFieldsFromLocaleTranslations,
} from "src/utils/localeTranslations";

const MenuItemOptionModal = React.memo(
  ({
    isOpen,
    onClose,
    menuItemOptionModalTitle,
    formik,
    menuItemOptionIndex,
    menuItemCorrelationId,
    getMenuItems,
  }: MenuItemOptionModalProps) => {
    const {
      isUpdatingOption,
      isAddingMenuItemOptionElement,
      updateMenuItemOptionMutation,
      addMenuItemOptionMutation,
      updateMenuItemOptionElementMutation,
      addMenuItemOptionElementMutation,
    } = useItemOptionMutations();

    const [displayPrices, setDisplayPrices] = useState<{
      [key: string]: string;
    }>({});
    const { enabledLocaleOptions, enabledLocales, defaultLocale } = useStorePosLocales();
    const [menuItemOptionElementsToRender, setMenuItemOptionElementsToRender] =
      useState<MenuItemOptionElement[]>([]);
    /** Index of element whose editor is expanded; null = none. New item is last index after add. */
    const [expandedElementIndex, setExpandedElementIndex] = useState<number | null>(null);
    const inlineEditorRef = useRef<HTMLDivElement>(null);

    const totalMenuItemOptionElements =
      menuItemOptionElementsToRender.length > 0
        ? menuItemOptionElementsToRender.length
        : 1;

    const {
      values: { itemOptionInputList },
    } = formik;
    const menuItemOption = itemOptionInputList[menuItemOptionIndex] as AddItemOptionInput;
    const elements = menuItemOption?.addItemOptionElementInputList ?? [];

    const initialOptionLocaleTranslations = React.useMemo(
      () =>
        menuItemOption?.localeTranslations ??
        buildLocaleTranslationMap(menuItemOption?.translations, enabledLocales, {
          englishName: menuItemOption?.name,
          nonEnglishName: menuItemOption?.nonEnglishName,
        }),
      [menuItemOption, enabledLocales]
    );
    const [optionLocaleTranslations, setOptionLocaleTranslations] =
      useState<LocaleTranslationMap>(initialOptionLocaleTranslations);

    useEffect(() => {
      setOptionLocaleTranslations(initialOptionLocaleTranslations);
    }, [initialOptionLocaleTranslations]);

    useEffect(() => {
      const menuItemOptionElements =
        itemOptionInputList[menuItemOptionIndex]
          ?.addItemOptionElementInputList ?? [];
      setMenuItemOptionElementsToRender(menuItemOptionElements);
    }, [itemOptionInputList, menuItemOptionIndex]);

    const syncOptionLegacyFields = useCallback(
      (next: LocaleTranslationMap) => {
        const legacy = syncLegacyNameFieldsFromLocaleTranslations(next);
        formik.setFieldValue(`itemOptionInputList[${menuItemOptionIndex}].name`, legacy.name);
        formik.setFieldValue(
          `itemOptionInputList[${menuItemOptionIndex}].nonEnglishName`,
          legacy.nonEnglishName
        );
        formik.setFieldValue(
          `itemOptionInputList[${menuItemOptionIndex}].translations`,
          buildTranslationsPayload(next, enabledLocales)
        );
        formik.setFieldValue(
          `itemOptionInputList[${menuItemOptionIndex}].localeTranslations`,
          next
        );
      },
      [formik, menuItemOptionIndex, enabledLocales]
    );

    const {
      addNewOptionElementToForm,
      onChangeElementValue,
      deleteOptionElementFromForm,
    } = useOptionElementHandlers(formik, menuItemOptionIndex);

    const {
      isUpdateButtonDisabled,
      isDoneButtonDisabled,
      handleSaveNewMenuItemOptionSuccess,
      handleSaveNewOptionElementSuccess,
      handleUpdateMenuItemOptionSuccess,
      handleUpdateMenuItemOptionElementSuccess,
      handleCloseModal,
    } = useMenuItemOptionModalLogic({
      formik,
      menuItemOption: {
        correlationId: menuItemOption?.correlationId || "",
        name: menuItemOption.name,
        description: menuItemOption.description,
        renderSequence: Number(menuItemOption.renderSequence),
        multipleSelectionsAllowed: !!menuItemOption.multipleSelectionsAllowed,
        isRequired: !!menuItemOption.isRequired,
      },
      menuItemOptionIndex,
      menuItemOptionElementsToRender,
      isUpdatingOption,
      getMenuItems,
    });

    const handleCloseModalCallback = useCallback(() => {
      handleCloseModal(onClose);
    }, [handleCloseModal, onClose]);

    const updateMenuItemOptionInDatabase = useCallback(async () => {
      await updateMenuItemOptionMutation(
        {
          itemOptionCorrelationId: menuItemOption.correlationId || "",
          name: menuItemOption.name,
          nonEnglishName: menuItemOption.nonEnglishName,
          description: menuItemOption.description,
          renderSequence: Number(menuItemOption.renderSequence),
          multipleSelectionsAllowed: !!menuItemOption.multipleSelectionsAllowed,
          isRequired: !!menuItemOption.isRequired,
          translations: buildTranslationsPayload(optionLocaleTranslations, enabledLocales),
        },
        {
          onSuccess: () => {
            handleUpdateMenuItemOptionSuccess();
            handleCloseModalCallback();
          },
        }
      );
    }, [updateMenuItemOptionMutation, menuItemOption, handleUpdateMenuItemOptionSuccess, handleCloseModalCallback]);

    const saveNewMenuItemOptionToDatabase = useCallback(async () => {
      if (!menuItemCorrelationId) return;
      await addMenuItemOptionMutation(menuItemOption, menuItemCorrelationId, {
        onSuccess: (data: AddMenuItemOptionSuccessPayload) => {
          handleSaveNewMenuItemOptionSuccess(data);
          handleCloseModalCallback();
        },
      });
    }, [addMenuItemOptionMutation, menuItemCorrelationId, menuItemOption, handleSaveNewMenuItemOptionSuccess, handleCloseModalCallback]);

    const updateMenuItemOptionElementInDatabase = useCallback(
      async (menuItemOptionElementIndex: number, isDeleted = false) => {
        const menuItemOptionElement =
          menuItemOption?.addItemOptionElementInputList?.[menuItemOptionElementIndex];
        if (!menuItemOptionElement) return;
        await updateMenuItemOptionElementMutation(menuItemOptionElement, isDeleted, {
          onSuccess: () => {
            handleUpdateMenuItemOptionElementSuccess(
              isDeleted,
              menuItemOptionElementIndex,
              deleteOptionElementFromForm
            );
            setExpandedElementIndex(null);
          },
        });
      },
      [
        updateMenuItemOptionElementMutation,
        menuItemOption,
        handleUpdateMenuItemOptionElementSuccess,
        deleteOptionElementFromForm,
      ]
    );

    const saveNewOptionElementToDatabase = useCallback(
      async (menuItemOptionElementIndex: number) => {
        const menuItemOptionElement =
          menuItemOption?.addItemOptionElementInputList?.[menuItemOptionElementIndex];
        if (!menuItemOption?.correlationId || !menuItemOptionElement) return;
        await addMenuItemOptionElementMutation(
          menuItemOption.correlationId,
          menuItemOptionElement,
          {
            onSuccess: (menuItemOptionElementCorrelationId) => {
              handleSaveNewOptionElementSuccess(
                menuItemOptionElementCorrelationId,
                menuItemOptionElementIndex
              );
              setExpandedElementIndex(null);
            },
          }
        );
      },
      [
        addMenuItemOptionElementMutation,
        menuItemOption,
        handleSaveNewOptionElementSuccess,
      ]
    );

    useEffect(() => {
      const newDisplayPrices: { [key: string]: string } = {};
      const els = menuItemOption?.addItemOptionElementInputList || [];
      els.forEach(
        (el: MenuItemOptionElement, idx: number) => {
          const priceInDollars = convertCentsToDollars(el.priceCents ?? 0);
          newDisplayPrices[`${menuItemOptionIndex}-${idx}`] = priceInDollars.toFixed(2);
        }
      );
      const count =
        menuItemOptionElementsToRender.length > 0
          ? menuItemOptionElementsToRender.length
          : els.length > 0
            ? els.length
            : 1;
      for (let i = 0; i < count; i++) {
        const key = `${menuItemOptionIndex}-${i}`;
        if (!newDisplayPrices[key]) newDisplayPrices[key] = "0.00";
      }
      setDisplayPrices(newDisplayPrices);
    }, [menuItemOption?.addItemOptionElementInputList, menuItemOptionIndex, menuItemOptionElementsToRender]);

    const handleAddItemClick = useCallback(() => {
      const currentLength = menuItemOption?.addItemOptionElementInputList?.length ?? 0;
      addNewOptionElementToForm();
      setExpandedElementIndex(currentLength);
    }, [menuItemOption?.addItemOptionElementInputList?.length, addNewOptionElementToForm]);

    useEffect(() => {
      if (expandedElementIndex !== null) {
        const id = window.setTimeout(
          () => inlineEditorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }),
          150
        );
        return () => window.clearTimeout(id);
      }
    }, [expandedElementIndex]);

    const handleEditElement = useCallback((index: number) => {
      setExpandedElementIndex(index);
    }, []);

    const handleDeleteElement = useCallback(
      (index: number) => {
        const el = menuItemOption?.addItemOptionElementInputList?.[index];
        if (el?.correlationId) {
          updateMenuItemOptionElementInDatabase(index, true);
        } else {
          deleteOptionElementFromForm(index);
          if (expandedElementIndex === index) setExpandedElementIndex(null);
          else if (expandedElementIndex != null && expandedElementIndex > index) {
            setExpandedElementIndex(expandedElementIndex - 1);
          }
        }
      },
      [
        menuItemOption?.addItemOptionElementInputList,
        updateMenuItemOptionElementInDatabase,
        deleteOptionElementFromForm,
        expandedElementIndex,
      ]
    );

    const handleInlineCancel = useCallback(() => {
      if (expandedElementIndex == null) return;
      const el = menuItemOption?.addItemOptionElementInputList?.[expandedElementIndex];
      if (!el?.correlationId) {
        deleteOptionElementFromForm(expandedElementIndex);
      }
      setExpandedElementIndex(null);
    }, [expandedElementIndex, menuItemOption?.addItemOptionElementInputList, deleteOptionElementFromForm]);

    const handleDoneEditingNewElement = useCallback(() => {
      setExpandedElementIndex(null);
    }, []);

    const hasOptionCorrelationId = !!menuItemOption?.correlationId;
    const isSaveOptionDisabled = hasOptionCorrelationId
      ? isUpdateButtonDisabled
      : isDoneButtonDisabled;

    const handleSaveOption = useCallback(() => {
      if (hasOptionCorrelationId) {
        updateMenuItemOptionInDatabase();
      } else {
        if (menuItemCorrelationId) {
          saveNewMenuItemOptionToDatabase();
        } else {
          handleCloseModalCallback();
        }
      }
    }, [
      hasOptionCorrelationId,
      menuItemCorrelationId,
      updateMenuItemOptionInDatabase,
      saveNewMenuItemOptionToDatabase,
      handleCloseModalCallback,
    ]);

    return (
      <Modal
        isOpen={isOpen}
        onClose={handleCloseModalCallback}
        title={menuItemOptionModalTitle}
        width="788px"
        maxWidth="788px"
        minWidth="10px"
        maxHeight="90dvh"
      >
        <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
          {/* Scrollable body */}
          <div
            className="flex-1 min-h-0 overflow-y-auto pr-1"
            style={{ scrollbarWidth: "thin" }}
          >
            {/* ——— Option ——— */}
            <p className="text-xs font-medium text-[#9B9B9B] uppercase tracking-wide mb-3">
              Option
            </p>
            <div className="space-y-3">
              <MenuItemLocaleFields
                enabledLocaleOptions={enabledLocaleOptions}
                defaultLocale={defaultLocale}
                localeTranslations={optionLocaleTranslations}
                onChange={(next: LocaleTranslationMap) => {
                  setOptionLocaleTranslations(next);
                  syncOptionLegacyFields(next);
                }}
                entityLabel="Option"
                showDescription={false}
              />
              <FormRowVertical label="Show sequence" required compact className="w-[88px]">
                <Input
                  type={InputTypeEnum.NUMBER}
                  min="0"
                  {...formik.getFieldProps(
                    `itemOptionInputList[${menuItemOptionIndex}].renderSequence`
                  )}
                  onChange={(e) =>
                    formik.setFieldValue(
                      `itemOptionInputList[${menuItemOptionIndex}].renderSequence`,
                      e.target.value === "" ? null : Number(e.target.value)
                    )
                  }
                />
              </FormRowVertical>
              <FormRowVertical label="Description" compact>
                <Input
                  as={InputAsEnum.TEXTAREA}
                  {...formik.getFieldProps(
                    `itemOptionInputList[${menuItemOptionIndex}].description`
                  )}
                />
              </FormRowVertical>
            </div>

            <p className="text-xs font-medium text-[#9B9B9B] uppercase tracking-wide mt-5 mb-3">
              Selection Rules
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between py-1">
                <span className="text-sm text-[#1A1A1A]">Required</span>
                <Toggle
                  checked={!!menuItemOption?.isRequired}
                  onChange={(value) =>
                    formik.setFieldValue(
                      `itemOptionInputList[${menuItemOptionIndex}].isRequired`,
                      value
                    )
                  }
                />
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-sm text-[#1A1A1A]">Allow multiple selections</span>
                <Toggle
                  checked={!!menuItemOption?.multipleSelectionsAllowed}
                  onChange={(value) =>
                    formik.setFieldValue(
                      `itemOptionInputList[${menuItemOptionIndex}].multipleSelectionsAllowed`,
                      value
                    )
                  }
                />
              </div>
            </div>

            {/* ——— Option Items ——— */}
            <p className="text-xs font-medium text-[#9B9B9B] uppercase tracking-wide mt-5 mb-3">
              Option Items ({elements.length})
            </p>
            <div className="space-y-1.5">
              {elements.map(
                (menuItemOptionElement: MenuItemOptionElement, menuItemOptionElementIndex: number) => {
                  const isExpanded = expandedElementIndex === menuItemOptionElementIndex;
                  const isNewUnsaved = !menuItemOptionElement?.correlationId;
                  const showRowOnly = !isExpanded || !isNewUnsaved;
                  const showEditorOnly = isExpanded && isNewUnsaved;
                  const showRowAndEditor = isExpanded && !isNewUnsaved;

                  return (
                    <div key={menuItemOptionElementIndex}>
                      {showRowOnly && (
                        <MenuItemOptionElementRow
                          element={menuItemOptionElement}
                          elementIndex={menuItemOptionElementIndex}
                          onEdit={handleEditElement}
                          onDelete={handleDeleteElement}
                        />
                      )}
                      {(showEditorOnly || showRowAndEditor) && (
                        <div
                          ref={isExpanded ? inlineEditorRef : undefined}
                          className="mt-1.5 ml-0 overflow-hidden transition-all duration-200 ease-out"
                        >
                          <MenuItemOptionElementForm
                            formik={formik}
                            displayPrices={displayPrices}
                            setDisplayPrices={setDisplayPrices}
                            menuItemOptionElement={menuItemOptionElement}
                            menuItemOptionElementIndex={menuItemOptionElementIndex}
                            menuItemOptionIndex={menuItemOptionIndex}
                            totalMenuItemOptionElements={Math.max(totalMenuItemOptionElements, elements.length)}
                            hasMenuItemOptionCorrelationId={hasOptionCorrelationId}
                            isAddingMenuItemOptionElement={isAddingMenuItemOptionElement}
                            onChangeElementValue={(
                              field: MenuItemOptionElementField,
                              value: InputValue,
                              idx: number
                            ) => onChangeElementValue(field, value, idx)}
                            updateMenuItemOptionElement={updateMenuItemOptionElementInDatabase}
                            removeMenuItemOptionElement={deleteOptionElementFromForm}
                            addNewElement={saveNewOptionElementToDatabase}
                            inlineMode
                            onCancel={handleInlineCancel}
                            onDoneEditingNewElement={
                              hasOptionCorrelationId ? undefined : handleDoneEditingNewElement
                            }
                          />
                        </div>
                      )}
                    </div>
                  );
                }
              )}

              {expandedElementIndex === null && (
                <button
                  type="button"
                  onClick={handleAddItemClick}
                  className="mt-1.5 inline-flex items-center gap-1 text-[13px] text-[#6b7280] hover:text-[#111827] hover:bg-[#f9fafb] border border-[#E5E7EB] hover:border-[#D1D5DB] rounded-md px-3 py-1.5 transition-colors focus:outline-none focus:ring-1 focus:ring-[#D1D5DB]"
                >
                  <span className="text-[16px] leading-none">+</span> Add Option Item
                </button>
              )}
            </div>
          </div>

          {/* Sticky footer */}
          <div className="flex-shrink-0 flex justify-end gap-3 pt-4 mt-4 border-t border-[#F1F1F2]">
            <Button variant="ghost" onClick={handleCloseModalCallback}>
              Cancel
            </Button>
            <Button
              variant="orange"
              isLoading={isUpdatingOption}
              disabled={isSaveOptionDisabled}
              onClick={handleSaveOption}
            >
              Save Option
            </Button>
          </div>
        </div>
      </Modal>
    );
  }
);

MenuItemOptionModal.displayName = "MenuItemOptionModal";

export default MenuItemOptionModal;
